import {
  assert,
  type Child,
  type Field,
  type Group,
  type SchemaDocument,
  type VersionChild,
} from "./project";

const IGNORE_FIELDS = ["label", "hidden", "locked"];

/**
 * Create a JSON schema from the provided xdf3 schema message.
 * TODO: Handle external code lists
 */
export function fromDocument(document: SchemaDocument): Record<string, any> {
  let id: string;
  if (document.schema.type === "version") {
    id = `${document.schema.fimId}v${document.schema.fimVersion}.schema.json`;
  } else {
    id = `${document.schema.fimId}.schema.json`;
  }

  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: id,
    title: document.schema.basicData.name,
    type: "object",
    $defs: {
      ..._create_field_defs(document.fields),
      ..._create_group_defs(document.groups, document.fields),
    },
    ..._get_properties(document.schema.children, document.fields),
  };
}

function _create_group_defs(
  groups: Record<string, Group>,
  fields: Record<string, Field>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [fimId, group] of Object.entries(groups)) {
    result[fimId] = {
      title: group.basicData.name,
      type: "object",
      ..._get_properties(group.children, fields),
    };
  }

  return result;
}

function _create_field_defs(
  fields: Record<string, Field>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [fimId, field] of Object.entries(fields)) {
    if (!IGNORE_FIELDS.includes(field.inputType)) {
      result[fimId] = {
        title: field.basicData.name,
        ..._get_field_type(field),
      };
    }
  }

  return result;
}

function _get_field_type(field: Field): Record<string, any> {
  if (field.inputType === "input") {
    return _get_input_properties(field);
  } else if (field.inputType === "select") {
    if (field.dataType !== "text") {
      console.warn(
        "Ignore select data type, use string instead [type=%s, id=%s]",
        field.dataType,
        field.fimId,
      );
    }

    const properties: Record<string, any> = { type: "string" };

    if (field.options.length > 0) {
      properties.enum = field.options.map((key, _value) => key);
    }

    return properties;
  } else {
    // Sanity check, this should never happen
    assert(
      false,
      `Datenfeld must be \`input\` or \`select\`, not ${field.inputType}`,
    );
  }
}

function _get_input_properties(field: Field): Record<string, any> {
  if (field.dataType === "text") {
    return {
      type: "string",
      ..._get_text_constraints(field),
    };
  } else if (field.dataType === "text_latin") {
    return {
      type: "string",
      ..._get_text_constraints(field),
    };
  } else if (field.dataType === "date") {
    return { type: "string", format: "date" };
  } else if (field.dataType === "time") {
    return { type: "string", format: "time" };
  } else if (field.dataType === "datetime") {
    return { type: "string", format: "date-time" };
  } else if (field.dataType === "bool") {
    return { type: "boolean" };
  } else if (field.dataType === "num") {
    return {
      type: "number",
      ..._get_number_constraints(field),
    };
  } else if (field.dataType === "num_int") {
    return {
      type: "integer",
      ..._get_number_constraints(field),
    };
  } else if (field.dataType === "num_currency") {
    return {
      type: "number",
      multipleOf: 0.01,
      ..._get_number_constraints(field),
    };
  } else {
    assert(field.dataType === "file" || field.dataType === "obj");
    return { type: "string" };
  }
}

function _get_text_constraints(field: Field): Record<string, any> {
  const result: Record<string, any> = {};
  const constraints = field.constraints;

  if (constraints.minLength !== undefined) {
    result.minLength = constraints.minLength;
  }

  if (constraints.maxLength !== undefined) {
    result.maxLength = constraints.maxLength;
  }

  if (constraints.pattern !== undefined) {
    result.pattern = constraints.pattern;
  }

  return result;
}

function _get_number_constraints(field: Field): Record<string, any> {
  const result: Record<string, any> = {};
  const constraints = field.constraints;

  if (constraints.minValue !== undefined) {
    result.minimum = _parse_float(field, constraints.minValue);
  }

  if (constraints.maxValue !== undefined) {
    result.maximum = _parse_float(field, constraints.maxValue);
  }

  return result;
}

function _parse_float(field: Field, value: string): number {
  try {
    return Number.parseFloat(delocalizeNumber(value));
  } catch (error) {
    throw new Error(
      `Invalid numeric limit '${value}' in field '${field.fimId}'`,
    );
  }
}

function _get_properties(
  children: Child[] | VersionChild[],
  fields: Record<string, Field>,
): Record<string, any> {
  const required: string[] = [];
  const properties: Record<string, any> = {};

  for (const child of children) {
    const childId = child.element.fimId;

    if (child.type === "field") {
      const field = fields[childId];
      if (IGNORE_FIELDS.includes(field.inputType)) {
        continue;
      }
    }

    if (child.cardinality.type === "array") {
      properties[childId] = {
        type: "array",
        items: { $ref: `#/$defs/${childId}` },
        minItems: child.cardinality.minSize,
      };

      if (child.cardinality.maxSize !== undefined) {
        properties[childId].maxItems = child.cardinality.maxSize;
      }
    } else {
      assert(child.cardinality.type === "value");
      properties[childId] = { $ref: `#/$defs/${childId}` };

      if (!child.cardinality.optional) {
        required.push(childId);
      }
    }
  }

  return {
    properties: properties,
    required: required,
  };
}

function delocalizeNumber(value: string): string {
  /**
   * Format a string with german separators into a parsable format by:
   *
   * 1. Removing the thousand separators
   * 2. Replacing the decimal point with a dot
   *
   * Example: 9.999,99 -> 9999.99
   */
  return value.replace(/\./g, "").replace(",", ".");
}
